/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import * as Sentry from '@sentry/browser';
import { useEffect, useState, useRef } from 'react';
import { setAppElement } from 'react-modal';
import { MeContext } from 'id-contexts/Me';
import { useQuery } from 'react-query';

import { UnauthenticatedApp } from 'id-components/UnauthenticatedApp';
import { AuthenticatedApp } from 'id-components/AuthenticatedApp';
import { ConfigContext, ConfigContextValue } from 'id-components/ConfigContext/ConfigContext';
import { getConfigs } from 'id-utils/config';
import { getMe } from 'id-api/auth';

export namespace App {
  export interface Props {}
}

export const App: React.FC<App.Props> = ({}) => {
  const [me, setMe] = useState<MeContext>(null);
  const [isLoading, setLoading] = useState<boolean>(true);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const { data: configData } = useQuery<Partial<ConfigContextValue>, Error>({
    queryKey: 'getConfig',
    queryFn: async () => {
      try {
        return getConfigs();
      } catch (err) {
        Sentry.captureException(err);
      }
    },
    // Always fall back to the most recent cache, even if it is old. This is different from
    // staleTime, which we keep to its default value to always eagerly fetch the data if possible.
    // If not possible, we will resort to the cache.
    cacheTime: Infinity,
    initialData: {
      featureFlags: {
        identityGeneralTerms20240617: false,
        identityGeneralTermsMultiTenant20240617: false,
        enable2026SettlementAgentWorkspacePrivacyPolicyAndTermsOfUse20260107: false,
        enable2026SettlementAgentWorkspacePrivacyPolicyAndTermsOfUseMultiTenant20260107: false,
      },
      auth: {
        saml: {
          tprSSO: {
            allowTPRSSO: false,
            tprUpgrade: false,
          },
        },
      },
    },
  });

  const fetchMe = async () => {
    try {
      const { data } = await getMe();
      Sentry.setUser({ id: data.userId });
      setMe(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchMe();
  }, []);

  const refCallback = (node: HTMLDivElement) => {
    // if we have already set the app element
    if (containerRef.current) return;
    containerRef.current = node;
    setAppElement(containerRef.current);
  };
  if (isLoading) return null;
  const isAuthed = !!me;
  const app = (() => {
    return isAuthed ? <AuthenticatedApp /> : <UnauthenticatedApp />;
  })();
  return (
    <div id="it-App" ref={refCallback}>
      <ConfigContext.Provider value={configData}>{app}</ConfigContext.Provider>
    </div>
  );
};
